import { MetricCard } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import type { FilterNames } from "app/custom-hooks/useSubtitleFilters";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter, minVisits } from "app/filters/ngFilters";
import { default as DurationService } from "app/services/DurationService";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../../../common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useActivityBreakdownData } from "../../../../common/overview/hooks/useActivityBreakdownData";
import { usePerformanceQuery } from "../../../../common/overview/hooks/usePerformanceQuery";
import { PerformanceEntitiesWidget } from "../../../../common/overview/PerformanceEntitiesWidget";
import { getDurationForTooltip } from "../../../../common/overview/PerformanceOverviewWidget";
import { defaultMinHeight, getEmptyState } from "../../../../common/overview/PerformanceWidget";
import type { PerformanceResponse } from "../../../../common/overview/types";
import type { AdIntelligenceController } from "../../../../common/shared/types/types";
import { NA_VALUE } from "../../../../common/utils/utils";
import { EDataType } from "../types";

import { SCREEN_WIDTH_THRESHOLD } from "./constants";
import { PerformanceLoadingState } from "./loading-pixel";
import { SpendAndImpressionsWidget } from "./spend-and-impression";
import { PerformanceOverTimeGraphContainer, PerformanceWidgetsWrapper, WidgetContentContainer } from "./styled";

interface IPreviewPerformanceWidgetProps {
    controller?: AdIntelligenceController;
    performanceOverTimeGraphComponent?: React.ReactNode;
}

const DEFAULT_SUBTITLE_FILTERS: FilterNames[] = ["webSource"];
export const PreviewPerformanceWidget = ({
    performanceOverTimeGraphComponent,
    controller = entityTypeMap.advertiser,
}: IPreviewPerformanceWidgetProps) => {
    const thirdRowEntity = controller === entityTypeMap.advertiser ? EDataType.VISITS : EDataType.PAGE_VIEWS;
    const { width } = useWindowSize();
    const isNarrowScreen = width !== undefined && width < SCREEN_WIDTH_THRESHOLD;
    const subtitleFilters = [...DEFAULT_SUBTITLE_FILTERS];
    if (!isNarrowScreen) {
        subtitleFilters.unshift("country");
    }

    const combinedSubtitleFilters = useSubtitleFilters(...subtitleFilters);
    const {
        isPending: performanceIsPending,
        isError: performanceIsError,
        data: performanceDataResponse,
    } = usePerformanceQuery(controller);
    const {
        data: entitiesData,
        error: entitiesError,
        isLoading: entitiesLoading,
    } = useActivityBreakdownData(controller);
    const { duration } = useDisplayAdvertiserPublisherDataAndParams();
    const { isWindow } = DurationService.getDurationData(duration).forAPI;

    const singleDomainData =
        performanceDataResponse?.records && typeof performanceDataResponse.records === "object"
            ? (Object.values(performanceDataResponse.records)[0] as PerformanceResponse | undefined)
            : undefined;

    const performanceData = singleDomainData
        ? { ...singleDomainData, hasAdIntelMetrics: performanceDataResponse.hasAdIntelMetrics }
        : undefined;
    const showEntitiesEmptyState =
        !entitiesLoading && Object.values(entitiesData ?? {}).every((value) => !value || typeof value !== "object");
    const showPerformanceEmptyState = !performanceIsPending && !performanceData?.[thirdRowEntity]?.value;

    return (
        <>
            <PerformanceWidgetsWrapper>
                <WidgetContainer
                    title={translate(`ad.intelligence.${controller}.overview.${thirdRowEntity}.widget.title`)}
                    tooltip={translate(
                        `ad.intelligence.${controller}.overview.performance.widget.${thirdRowEntity}.tooltip`,
                    )}
                    subtitleFilters={combinedSubtitleFilters}
                >
                    <WidgetContentContainer>
                        {showPerformanceEmptyState ? (
                            getEmptyState(performanceIsError, true, defaultMinHeight)
                        ) : performanceIsPending ? (
                            <PerformanceLoadingState />
                        ) : (
                            <MetricCard
                                withoutShadow
                                value={
                                    <Text
                                        fontVariant="h3"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {minVisits(
                                            performanceData?.[thirdRowEntity]?.value,
                                            1000,
                                            abbrNumberFilter(),
                                            NA_VALUE,
                                        )}
                                    </Text>
                                }
                                {...(performanceData?.[thirdRowEntity]?.change && {
                                    changeValueProps: {
                                        value: minimumValueChangeFilter()(performanceData?.[thirdRowEntity]?.change, 1),
                                        isDecrease: performanceData?.[thirdRowEntity]?.change < 0,
                                        tooltipProps: {
                                            enabled: true,
                                            tooltipContent: isWindow
                                                ? translate(
                                                      "ad.intelligence.overview.performance.widget.window.change.tooltip",
                                                  )
                                                : translate(
                                                      `ad.intelligence.overview.performance.widget.${thirdRowEntity}.change.tooltip`,
                                                      getDurationForTooltip(duration),
                                                  ),
                                        },
                                    },
                                })}
                            />
                        )}
                    </WidgetContentContainer>
                </WidgetContainer>
                <SpendAndImpressionsWidget
                    controller={controller}
                    combinedSubtitleFilters={combinedSubtitleFilters}
                    showPerformanceEmptyState={showPerformanceEmptyState}
                />
                <WidgetContainer
                    title={translate(`ad.intelligence.${controller}.overview.activity.breakdown.widget.title`)}
                    subtitleFilters={combinedSubtitleFilters}
                >
                    <WidgetContentContainer>
                        {showEntitiesEmptyState ? (
                            getEmptyState(entitiesError, true, defaultMinHeight)
                        ) : (
                            <PerformanceEntitiesWidget
                                data={entitiesData}
                                isLoading={entitiesLoading}
                                controller={controller}
                                isWindow={isWindow}
                            />
                        )}
                    </WidgetContentContainer>
                </WidgetContainer>
            </PerformanceWidgetsWrapper>
            <PerformanceOverTimeGraphContainer>{performanceOverTimeGraphComponent}</PerformanceOverTimeGraphContainer>
        </>
    );
};
